import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

export interface Language {
  code: string;
  label: string;
  flagCode: string;
}

export const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'en', label: 'English', flagCode: 'gb' },
  { code: 'zh', label: '中文 (Mandarin)', flagCode: 'cn' },
  { code: 'ja', label: '日本語 (Japanese)', flagCode: 'jp' },
  { code: 'pt', label: 'Português', flagCode: 'br' },
  { code: 'es', label: 'Español', flagCode: 'es' },
];

const STORAGE_KEY = 'selectedLanguage';

export interface LanguageFeatureFlags {
  langZh: boolean;
  langJa: boolean;
  langPt: boolean;
  langEs: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private currentLanguage = new BehaviorSubject<Language>(SUPPORTED_LANGUAGES[0]);
  public currentLanguage$ = this.currentLanguage.asObservable();

  private enabledFlags: LanguageFeatureFlags = { langZh: true, langJa: true, langPt: true, langEs: true };
  private availableLanguages = new BehaviorSubject<Language[]>(SUPPORTED_LANGUAGES);
  public availableLanguages$ = this.availableLanguages.asObservable();

  constructor(private translocoService: TranslocoService) {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const match = SUPPORTED_LANGUAGES.find(l => l.code === saved);
      if (match) {
        this.currentLanguage.next(match);
      }
    }

    this.translocoService.setActiveLang(this.currentLanguage.value.code);
  }

  get language(): Language {
    return this.currentLanguage.value;
  }

  get languages(): Language[] {
    return this.availableLanguages.value;
  }

  async initializeLanguage(): Promise<void> {
    const activeLanguage = this.currentLanguage.value.code;
    this.translocoService.setActiveLang(activeLanguage);
    await firstValueFrom(this.translocoService.load(activeLanguage));
  }

  setLanguage(lang: Language): void {
    this.currentLanguage.next(lang);
    this.translocoService.setActiveLang(lang.code);
    localStorage.setItem(STORAGE_KEY, lang.code);
  }

  updateFeatureFlags(flags: LanguageFeatureFlags): void {
    this.enabledFlags = flags;
    const flagMap: Record<string, boolean> = {
      zh: flags.langZh,
      ja: flags.langJa,
      pt: flags.langPt,
      es: flags.langEs,
    };

    const filtered = SUPPORTED_LANGUAGES.filter(l => l.code === 'en' || flagMap[l.code]);
    this.availableLanguages.next(filtered);

    // If user's current language got disabled, fall back to English
    const current = this.currentLanguage.value;
    if (current.code !== 'en' && !flagMap[current.code]) {
      this.setLanguage(SUPPORTED_LANGUAGES[0]);
    }
  }
}
